import { supabase } from './supabase';
import { PostgrestError } from '@supabase/supabase-js';
import { getIsAuthenticated } from './auth';

export interface Listing {
  id: string;
  user_id: string;
  owner: {
    display_name?: string;
    full_name?: string;
    avatar_url?: string;
    rating: number;
    review_count: number;
  };
  title: string;
  description: string;
  brand: string;
  condition: string;
  price: number;
  original_price: number;
  type: 'sale' | 'trade' | 'both';
  color: string;
  fiber_type?: string;
  cap_size?: string;
  modifications?: string;
  status: 'active' | 'sold' | 'traded' | 'deleted' | 'removed' | 'permanently_deleted';
  created_at: string;
  updated_at: string;
  removed_at?: string;
  permanent_delete_at?: string;
  removed_by?: string;
  image_urls?: string[];
  slug?: string;
  // Phase 2: store (ClearanceWigs open-box) listings.
  source?: 'user' | 'clearancewigs';
  external_url?: string;
  is_official?: boolean;
  is_available?: boolean;
}

export interface CreateListingData {
  title: string;
  description: string;
  brand: string;
  condition: string;
  price: number;
  original_price: number;
  type: 'sale' | 'trade' | 'both';
  color: string;
  fiber_type: string;
  cap_size: string;
  modifications: string;
  images: File[];
  existing_image_urls?: string[];
}

export interface ListingFilters {
  brand?: string;
  condition?: string;
  minPrice?: number;
  maxPrice?: number;
  type?: 'sale' | 'trade' | 'both';
  color?: string;
  status?: 'active' | 'sold' | 'traded';
  sort?: 'newest' | 'price_asc' | 'price_desc';
  source?: 'user' | 'clearancewigs';
  limit?: number;
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// ── ClearanceWigs store-listing badge (open-box vs outlet) ──────────────────────
// Owner clarification 2026-07-12: only products in the `clearance-wigs-sale`
// collection are true OPEN-BOX (condition='open-box'); every other ClearanceWigs
// product is a NEW wig at an OUTLET PRICE (condition='new'). Copy must stay honest.
export function storeBadge(listing: Pick<Listing, 'source' | 'condition'>): 'Open-box' | 'Outlet price' | null {
  if (listing.source !== 'clearancewigs') return null;
  return listing.condition === 'open-box' ? 'Open-box' : 'Outlet price';
}

/** Longer CTA/microcopy variant for store listings. */
export function storeCtaLabel(listing: Pick<Listing, 'source' | 'condition'>): string | null {
  const badge = storeBadge(listing);
  if (!badge) return null;
  return badge === 'Open-box' ? 'Shop open-box deal' : 'Shop outlet deal';
}

// Map a flat row from the public RPCs (no PII) into the Listing shape the UI uses.
// user_id is intentionally NOT exposed to anonymous callers, so it is left blank;
// components must guard `listing.user_id` before using it (reviews / seller links).
export function mapPublicRow(row: any): Listing {
  return {
    id: row.id,
    user_id: '',
    owner: {
      display_name: row.owner_display_name || undefined,
      full_name: row.owner_display_name || undefined,
      avatar_url: row.owner_avatar_url || undefined,
      rating: Number(row.owner_rating) || 0,
      review_count: Number(row.owner_review_count) || 0,
    },
    title: row.title,
    description: row.description ?? '',
    brand: row.brand,
    condition: row.condition,
    price: row.price,
    original_price: row.original_price,
    type: row.type,
    color: row.color,
    fiber_type: row.fiber_type ?? undefined,
    cap_size: row.cap_size ?? undefined,
    modifications: row.modifications ?? undefined,
    status: row.status ?? 'active',
    created_at: row.created_at,
    updated_at: row.updated_at ?? row.created_at,
    image_urls: row.image_urls || [],
    slug: row.slug ?? undefined,
    source: row.source ?? 'user',
    external_url: row.external_url ?? undefined,
    is_official: row.is_official ?? false,
  };
}

// Public (anonymous) catalog read via the SECURITY DEFINER RPC. Returns total for pagination.
export async function getPublicListings(
  filters: ListingFilters = {},
  pagination: { limit?: number; offset?: number } = {}
): Promise<{ listings: Listing[]; total: number; error: PostgrestError | null }> {
  const { data, error } = await supabase.rpc('get_public_listings', {
    p_brand: filters.brand ?? null,
    p_search: null,
    p_color: filters.color ?? null,
    p_min: filters.minPrice ?? null,
    p_max: filters.maxPrice ?? null,
    p_sort: filters.sort ?? 'newest',
    p_condition: filters.condition ?? null,
    p_type: filters.type ?? null,
    p_limit: pagination.limit ?? 40,
    p_offset: pagination.offset ?? 0,
    p_source: filters.source ?? null,
  });

  if (error) {
    console.error('Error fetching public listings:', error);
    return { listings: [], total: 0, error };
  }

  const rows = data || [];
  const total = rows.length > 0 ? Number(rows[0].total_count) : 0;
  return { listings: rows.map(mapPublicRow), total, error: null };
}

export async function getListings(filters: ListingFilters = {}): Promise<{ listings: Listing[]; error: PostgrestError | null }> {
  // Check if user is authenticated
  const isAuthenticated = await getIsAuthenticated();

  // Anonymous visitors read the public, PII-safe catalog via RPC (Phase 1 de-gate).
  if (!isAuthenticated) {
    const { listings, error } = await getPublicListings(filters, { limit: filters.limit ?? 40 });
    return { listings, error };
  }

  let query = supabase
    .from('listings')
    .select('*, owner:listing_owners!user_id(*)')
    .eq('status', filters.status || 'active');

  if (filters.brand) {
    query = query.eq('brand', filters.brand);
  }

  if (filters.condition) {
    query = query.eq('condition', filters.condition);
  }

  if (filters.type) {
    query = query.eq('type', filters.type);
  }

  if (filters.color) {
    query = query.eq('color', filters.color);
  }

  if (filters.source) {
    query = query.eq('source', filters.source);
  }

  if (filters.minPrice) {
    query = query.gte('price', filters.minPrice);
  }

  if (filters.maxPrice) {
    query = query.lte('price', filters.maxPrice);
  }

  switch (filters.sort) {
    case 'price_asc':
      query = query.order('price', { ascending: true });
      break;
    case 'price_desc':
      query = query.order('price', { ascending: false });
      break;
    default:
      query = query.order('created_at', { ascending: false });
  }

  // Bound the fetch — an unfiltered call must not pull the whole catalog.
  query = query.limit(filters.limit ?? 60);

  const { data, error } = await query;
  
  if (error) {
    console.error('Error fetching listings:', error);
  }

  return { listings: data || [], error };
}

// Helper function to convert any error to PostgrestError format
function toPostgrestError(error: Error | null): PostgrestError | null {
  if (!error) return null;
  
  return {
    message: error.message,
    details: '',
    hint: '',
    code: 'AUTH_ERROR',
    name: 'AuthError'
  };
}

export async function getSavedListings(): Promise<{ listings: Listing[]; error: PostgrestError | null }> {
  const { data: { user }, error: userError } = await supabase.auth.getUser();
  
  if (userError) return { listings: [], error: toPostgrestError(userError) };
  if (!user) return { listings: [], error: toPostgrestError(new Error('Not authenticated')) };

  // Get all saved listing IDs for the current user
  const { data: savedListings, error: savedError } = await supabase
    .from('saved_listings')
    .select('listing_id')
    .eq('user_id', user.id)
    .order('created_at', { ascending: false });

  if (savedError) return { listings: [], error: savedError };
  if (!savedListings || savedListings.length === 0) return { listings: [], error: null };

  // Extract listing IDs
  const listingIds = savedListings.map(item => item.listing_id);

  // Get the actual listings
  const { data: listings, error } = await supabase
    .from('listings')
    .select('*')
    .in('id', listingIds);

  return { 
    listings: listings || [], 
    error 
  };
}

export async function unsaveListing(listingId: string): Promise<{ error: PostgrestError | null }> {
  const { data: { user }, error: userError } = await supabase.auth.getUser();
  
  if (userError) return { error: toPostgrestError(userError) };
  if (!user) return { error: toPostgrestError(new Error('Not authenticated')) };

  const { error } = await supabase
    .from('saved_listings')
    .delete()
    .eq('listing_id', listingId)
    .eq('user_id', user.id);

  return { error };
}

export async function deleteListing(id: string): Promise<{ error: PostgrestError | null }> {
  const { data: { user }, error: userError } = await supabase.auth.getUser();
  
  if (userError) return { error: toPostgrestError(userError) };
  if (!user) return { error: toPostgrestError(new Error('Not authenticated')) };

  const { error } = await supabase
    .from('listings')
    .update({ status: 'deleted' })
    .eq('id', id)
    .eq('user_id', user.id);

  return { error };
}

export async function removeListing(id: string): Promise<{ error: PostgrestError | null }> {
  const { data: { user }, error: userError } = await supabase.auth.getUser();
  
  if (userError) return { error: toPostgrestError(userError) };
  if (!user) return { error: toPostgrestError(new Error('Not authenticated')) };

  console.log('Calling remove_user_listing with ID:', id);
  const { data, error } = await supabase
    .rpc('remove_user_listing', { listing_id: id });

  console.log('RPC response:', { data, error });
  
  if (error) {
    console.error('RPC error:', error);
    return { error };
  }
  
  // The function returns a table, so data should be an array
  // If no rows returned, it means the listing wasn't found or couldn't be removed
  if (!data || data.length === 0) {
    return { error: toPostgrestError(new Error('Listing not found or cannot be removed')) };
  }

  console.log('Remove successful, returned data:', data[0]);
  return { error: null };
}

export async function restoreListing(id: string): Promise<{ error: PostgrestError | null }> {
  const { data: { user }, error: userError } = await supabase.auth.getUser();
  
  if (userError) return { error: toPostgrestError(userError) };
  if (!user) return { error: toPostgrestError(new Error('Not authenticated')) };

  console.log('Calling restore_user_listing with ID:', id);
  const { data, error } = await supabase
    .rpc('restore_user_listing', { listing_id: id });

  console.log('Restore RPC response:', { data, error });
  
  if (error) {
    console.error('Restore RPC error:', error);
    return { error };
  }
  
  // The function returns a table, so data should be an array
  if (!data || data.length === 0) {
    return { error: toPostgrestError(new Error('Listing not found or cannot be restored')) };
  }

  console.log('Restore successful, returned data:', data[0]);
  return { error: null };
}

export async function getRemovedListings(): Promise<{ listings: Listing[]; error: PostgrestError | null }> {
  const { data: { user }, error: userError } = await supabase.auth.getUser();
  
  if (userError) return { listings: [], error: toPostgrestError(userError) };
  if (!user) return { listings: [], error: toPostgrestError(new Error('Not authenticated')) };

  const { data, error } = await supabase
    .from('user_removed_listings')
    .select('*')
    .order('removed_at', { ascending: false });

  return { listings: data || [], error };
}

export async function bumpListing(id: string): Promise<{ error: PostgrestError | null }> {
  const { data: { user }, error: userError } = await supabase.auth.getUser();
  
  if (userError) return { error: toPostgrestError(userError) };
  if (!user) return { error: toPostgrestError(new Error('Not authenticated')) };

  const { error } = await supabase
    .from('listings')
    .update({ updated_at: new Date().toISOString() })
    .eq('id', id)
    .eq('user_id', user.id)
    .eq('status', 'active');

  return { error };
}

export async function markListingAsSold(id: string): Promise<{ error: PostgrestError | null }> {
  const { data: { user }, error: userError } = await supabase.auth.getUser();
  
  if (userError) return { error: toPostgrestError(userError) };
  if (!user) return { error: toPostgrestError(new Error('Not authenticated')) };

  const { error } = await supabase
    .from('listings')
    .update({ status: 'sold' })
    .eq('id', id)
    .eq('user_id', user.id)
    .eq('status', 'active');

  return { error };
}

export async function reactivateListing(id: string): Promise<{ error: PostgrestError | null }> {
  const { data: { user }, error: userError } = await supabase.auth.getUser();
  
  if (userError) return { error: toPostgrestError(userError) };
  if (!user) return { error: toPostgrestError(new Error('Not authenticated')) };

  const { error } = await supabase
    .from('listings')
    .update({ status: 'active' })
    .eq('id', id)
    .eq('user_id', user.id)
    .eq('status', 'sold');

  return { error };
}

// Upload listing images sequentially with progress reporting. If any upload
// fails, the ones already uploaded in this batch are removed from storage so
// nothing is orphaned, then the error is rethrown.
export type UploadProgress = (uploaded: number, total: number) => void;

async function uploadListingImagesBatch(
  userId: string,
  images: File[],
  onProgress?: UploadProgress
): Promise<string[]> {
  const urls: string[] = [];
  const uploadedPaths: string[] = [];
  try {
    for (let i = 0; i < images.length; i++) {
      const image = images[i];
      const timestamp = Date.now();
      const randomId = Math.random().toString(36).substring(7);
      const fileExt = image.name.split('.').pop() || 'jpg';
      const fileName = `${userId}/${timestamp}-${randomId}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('listing-images')
        .upload(fileName, image);
      if (uploadError) throw uploadError;

      uploadedPaths.push(fileName);
      const { data } = supabase.storage.from('listing-images').getPublicUrl(fileName);
      if (data?.publicUrl) urls.push(data.publicUrl);
      onProgress?.(i + 1, images.length);
    }
    return urls;
  } catch (error) {
    if (uploadedPaths.length > 0) {
      // Best-effort cleanup of the partial batch.
      await supabase.storage.from('listing-images').remove(uploadedPaths).catch(() => {});
    }
    throw error;
  }
}

export async function createListing(data: CreateListingData, onProgress?: UploadProgress): Promise<{ listing: Listing | null; error: PostgrestError | null }> {
  try {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError || !user) {
      console.error('Authentication error:', userError);
      return { listing: null, error: new Error('Not authenticated') as PostgrestError };
    }

    // Upload images first to get URLs (partial batches are cleaned up on failure)
    const imageUrls: string[] =
      data.images.length > 0
        ? await uploadListingImagesBatch(user.id, data.images, onProgress)
        : [];

    // Create listing with all required fields and image URLs
    console.log('Creating listing with data:', {
      ...data,
      imageUrls,
      userId: user.id
    });

    const { data: listing, error } = await supabase
      .from('listings')
      .insert({
        user_id: user.id,
        title: data.title,
        description: data.description,
        brand: data.brand,
        condition: data.condition,
        price: data.price,
        original_price: data.original_price,
        type: data.type,
        color: data.color,
        fiber_type: data.fiber_type,
        cap_size: data.cap_size,
        modifications: data.modifications,
        status: 'active',
        image_urls: imageUrls,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      })
      .select()
      .single();

    if (error) {
      console.error('Error creating listing:', error);
      throw error;
    }

    console.log('Listing created successfully:', listing);

    return { listing, error: null };
  } catch (error) {
    console.error('Error in createListing:', error);
    return { listing: null, error: error as PostgrestError };
  }
}

// `idOrSlug` may be a raw UUID (legacy links) or an SEO slug (Phase 1.4).
export async function getListing(idOrSlug: string): Promise<{ listing: Listing | null; error: PostgrestError | null }> {
  const isAuthenticated = await getIsAuthenticated();

  // Anonymous visitors read via the public detail RPC (resolves slug OR uuid, no PII).
  if (!isAuthenticated) {
    const { data, error } = await supabase.rpc('get_public_listing_detail', { p_key: idOrSlug });
    if (error) {
      console.error('Error fetching public listing detail:', error);
      return { listing: null, error };
    }
    const row = Array.isArray(data) ? data[0] : data;
    return { listing: row ? mapPublicRow(row) : null, error: null };
  }

  // Authenticated: keep the full embed (includes user_id for messaging/reviews).
  const isUuid = UUID_RE.test(idOrSlug);
  const { data, error } = await supabase
    .from('listings')
    .select('*, owner:listing_owners!user_id(*)')
    .eq(isUuid ? 'id' : 'slug', idOrSlug)
    .maybeSingle();

  return { listing: data, error };
}

export async function updateListing(id: string, updates: Partial<CreateListingData> & { existing_image_urls?: string[] }, onProgress?: UploadProgress): Promise<{ listing: Listing | null; error: PostgrestError | null }> {
  try {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError || !user) {
      return { listing: null, error: new Error('Not authenticated') as PostgrestError };
    }

    // First update the listing data
    const { data: listing, error } = await supabase
      .from('listings')
      .update({
        title: updates.title,
        description: updates.description,
        brand: updates.brand,
        condition: updates.condition,
        price: updates.price,
        original_price: updates.original_price,
        type: updates.type,
        color: updates.color,
        fiber_type: updates.fiber_type,
        cap_size: updates.cap_size,
        modifications: updates.modifications
      })
      .eq('id', id)
      .eq('user_id', user.id)
      .select()
      .single();

    if (error) throw error;

    // Handle image uploads and updates
    if (listing) {
      // Determine which existing images to keep
      let existingImageUrls: string[] = [];
      
      // If existing_image_urls was provided (from frontend), use those
      if (updates.existing_image_urls) {
        console.log('Using provided existing image URLs:', updates.existing_image_urls);
        existingImageUrls = updates.existing_image_urls;
      } else {
        // Otherwise get current images from database
        const { data: currentListing } = await supabase
          .from('listings')
          .select('image_urls')
          .eq('id', listing.id)
          .single();
        existingImageUrls = currentListing?.image_urls || [];
        console.log('Fetched existing image URLs from DB:', existingImageUrls);
      }
      
      // Create array for new image URLs
      const newImageUrls: string[] = [];
      
      // Process and upload new images if provided (partial batches cleaned up)
      if (updates.images && updates.images.length > 0) {
        newImageUrls.push(
          ...(await uploadListingImagesBatch(user.id, updates.images, onProgress))
        );
      }

      // Combine existing and new image URLs
      const combinedImageUrls = [...existingImageUrls, ...newImageUrls];
      console.log('Saving combined image URLs:', combinedImageUrls);
      
      // Always update the image_urls if we're handling images
      // whether or not new images were uploaded
      const { error: updateError } = await supabase
        .from('listings')
        .update({ image_urls: combinedImageUrls })
        .eq('id', listing.id)
        .eq('user_id', user.id);

      if (updateError) throw updateError;
    }

    return { listing, error: null };
  } catch (error) {
    console.error('Error updating listing:', error);
    return { listing: null, error: error as PostgrestError };
  }
}

export async function getUserListings(): Promise<{ listings: Listing[]; error: PostgrestError | null }> {
  // Check if user is authenticated - this is redundant here since this function
  // is only called from protected routes, but added for consistency
  const isAuthenticated = await getIsAuthenticated();
  
  if (!isAuthenticated) {
    return { listings: [], error: null };
  }
  const { data: { user }, error: userError } = await supabase.auth.getUser();
  
  if (userError) return { listings: [], error: toPostgrestError(userError) };
  if (!user) return { listings: [], error: toPostgrestError(new Error('Not authenticated')) };

  const { data, error } = await supabase
    .from('listings')
    .select('*, owner:listing_owners!user_id(*)')
    .eq('user_id', user.id)
    .in('status', ['active', 'sold', 'traded'])
    .order('created_at', { ascending: false });

  return { listings: data || [], error };
}
